import { TIMELINE, VALUES } from '../mock';
import { Target, Compass } from 'lucide-react';
import { motion } from 'framer-motion';
import { containerVariants, fadeUpVariants } from '../lib/motion';

export default function AboutPage() {
  return (
    <>
      <section className="pt-36 pb-16 bg-brand-mint relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-50" />
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative max-w-4xl mx-auto px-6 text-center"
        >
          <h1 className="heading-display text-4xl md:text-6xl text-brand-dark">Powering India's Evolving Mobility Needs</h1>
          <p className="mt-4 text-brand-dark/70 max-w-2xl mx-auto">Building one connected EV platform across delivery, campus mobility and everyday commute.</p>
        </motion.div>
      </section>

      <section className="section-pad bg-white">
        <motion.div 
          variants={containerVariants(0.15)}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          <motion.div key="mission" variants={fadeUpVariants(25, 0.7)}>
            <div className="bg-brand-lavenderSoft rounded-3xl p-8 border border-brand-dark/5 shadow-sm hover:shadow-lg hover:-translate-y-1.5 transition-all duration-300 h-full">
              <div className="w-12 h-12 rounded-xl bg-brand-dark text-brand-lime flex items-center justify-center mb-4 shadow-md"><Target size={22} /></div>
              <h2 className="heading-display text-2xl text-brand-dark">Our Mission</h2>
              <p className="text-brand-dark/70 mt-3 leading-relaxed">Making electric mobility available, accessible and affordable.</p>
            </div>
          </motion.div>
          
          <motion.div key="vision" variants={fadeUpVariants(25, 0.7)}>
            <div className="bg-brand-lavenderSoft rounded-3xl p-8 border border-brand-dark/5 shadow-sm hover:shadow-lg hover:-translate-y-1.5 transition-all duration-300 h-full">
              <div className="w-12 h-12 rounded-xl bg-brand-dark text-brand-lime flex items-center justify-center mb-4 shadow-md"><Compass size={22} /></div>
              <h2 className="heading-display text-2xl text-brand-dark">Our Vision</h2>
              <p className="text-brand-dark/70 mt-3 leading-relaxed">To build one mobility platform powering mobility across verticals.</p>
            </div>
          </motion.div>
        </motion.div>
      </section>

      <section className="py-16 bg-brand-lavender">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-4xl mx-auto px-6 text-center"
        >
          <h2 className="heading-display text-3xl md:text-4xl text-brand-dark">Our Story</h2>
          <p className="text-brand-dark/70 mt-5 leading-relaxed">
            E Drives began with a simple goal — to make electric mobility work in the real world. Starting with last-mile delivery, we worked closely with riders and experienced first-hand the challenges of access, affordability and everyday mobility operations.
          </p>
          <p className="text-brand-dark/70 mt-4 leading-relaxed">
            Those experiences shaped what E Drives is today — a mobility platform built to power different mobility verticals. From delivery to campuses and everyday commute, we continue to build mobility around where and how it is needed.
          </p>
        </motion.div>
      </section>

      <section className="section-pad bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="heading-display text-3xl md:text-4xl text-brand-dark text-center mb-12">Our Journey</h2>
          <div className="relative">
            <div className="absolute left-6 top-2 bottom-2 w-px bg-brand-dark/20" />
            
            <motion.div 
              variants={containerVariants(0.15)}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="space-y-6"
            >
              {TIMELINE.map((t) => (
                <motion.div key={t.year} variants={fadeUpVariants(25, 0.7)}>
                  <div className="pl-20 relative hover:translate-x-1.5 transition-all duration-200">
                    <span className="absolute left-0 top-0 w-12 h-12 rounded-full bg-brand-dark text-brand-lime flex items-center justify-center font-bold heading-display text-sm shadow-md">
                      {t.year}
                    </span>
                    <div className="bg-brand-lavenderSoft rounded-2xl p-5 border border-brand-dark/5 shadow-sm hover:shadow-md transition-shadow">
                      <h4 className="font-bold text-brand-dark text-base">{t.title}</h4>
                      <p className="text-brand-dark/75 mt-1 text-sm leading-relaxed">{t.text}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section-pad bg-brand-lavender">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="heading-display text-3xl md:text-4xl text-brand-dark text-center mb-12">What Drives Us</h2>
          
          <motion.div 
            variants={containerVariants(0.15)}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-3 gap-5"
          >
            {VALUES.map(({ icon: Icon, title, desc }) => (
              <motion.div key={title} variants={fadeUpVariants(25, 0.7)}>
                <div className="bg-white rounded-3xl p-7 border border-brand-dark/5 shadow-sm hover:shadow-lg hover:shadow-brand-lime/10 hover:border-brand-lime/30 hover:-translate-y-1.5 hover:scale-[1.01] transition-all duration-300 h-full">
                  <div className="w-12 h-12 rounded-xl bg-brand-mint flex items-center justify-center text-brand-dark mb-4 shadow-sm"><Icon size={22} /></div>
                  <h3 className="heading-display text-xl text-brand-dark">{title}</h3>
                  <p className="text-brand-dark/65 mt-2 text-sm leading-relaxed">{desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </>
  );
}
